import { smIconSize } from '@/constants';
import type { InstrumentRunPublic, SampleProteoformPublic } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { Download } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';

import { buildRunSampleDetailsColDefs } from './colDefs';

interface Props {
  orgId: string;
  run: InstrumentRunPublic;
}

// TODO: switch to rowModelType="serverSide" with createAgGridDatasource once
//  orgs/${orgId}/instrument_runs/${runId}/sample_proteoforms/search ships.
//  Use instrumentRunQueryKeys.endpoints.proteoformsSearch(orgId, run.id) for the URL.

const PLACEHOLDER = '—';

const RunSampleDetailsSection = ({ run }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'sampleDetails' });
  // i18next's strongly-typed t only accepts the known key union; bridge to the
  // plain (key: string) => string signature that colDefs expects.
  const colDefs = useMemo(() => buildRunSampleDetailsColDefs(key => t(key as never)), [t]);

  return (
    <Stack className={styles.sampleDetailsContainer} gap={3} orientation="vertical">
      {/* Title row */}
      <Stack className={styles.sampleDetailsTitleRow} orientation="horizontal">
        <Text as="h3">{t('title')}</Text>

        <Stack className={styles.statsRow} gap={2} orientation="horizontal">
          <Tooltip
            content={t('download_coming_soon')}
            placement="bottom"
            trigger={
              <Button data-testid="run-sample-details-download" isDisabled visual="unstyled">
                <Download size={smIconSize} />
                {t('download')}
              </Button>
            }
          />

          {/* TODO: Proteoforms total from pagination.total once run-scoped endpoint ships */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_proteoforms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* TODO: PTMs count — no API backing yet */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_ptms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* TODO: Isoforms count — no API backing yet */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_isoforms')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>

          {/* TODO: Threshold — no API field on run or org yet */}
          <Stack className={styles.statPill} gap={1} orientation="horizontal">
            <Text size="xs">{t('stat_threshold')}</Text>
            <Text size="xs">{PLACEHOLDER}</Text>
          </Stack>
        </Stack>
      </Stack>

      {/* Placeholder grid — rowData={[]} until run-scoped proteoform endpoint exists */}
      <Table
        columnDefs={colDefs}
        defaultColDef={{ resizable: true }}
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        pagination
        paginationPageSize={25}
        paginationPageSizeSelector={[25, 50, 100]}
        rowData={[] as SampleProteoformPublic[]}
        tableId={`run-sample-details-${run.id}`}
      />
    </Stack>
  );
};

export default RunSampleDetailsSection;
